const React = require('react')
const { useState } = require('react')
const { useHistory, useLocation } = require('react-router-dom')
const { Button, FormGroup, Label, Input, FormFeedback, UncontrolledTooltip } = require('reactstrap')
const { useResponsive, BREAKPOINTS } = require('@hooks')

const { useNetworkConfig } = require('@contexts')
const { solidityVersionOptions } = require('@constants/solidityVersions.constant')

const { VerifyContractFormGroup } = require('./formGroup.verifyContract')

require('@styles/pages/verifyContract/index.css')

const VerifyContract = () => {
    const history = useHistory()
    const { network } = useNetworkConfig()
    const location = useLocation()
    const params = new URLSearchParams(location.search)

    const isMobile = useResponsive(BREAKPOINTS.large)

    const [contractAddress, setContractAddress] = useState(params.get('a') || '')
    const [errors, setErrors] = useState({
        contractAddress: '',
        compilerType: '',
        compilerVersion: '',
        licenseType: ''
    })
    const [compilerType, setCompilerType] = useState('Please Select')
    const [compilerVersion, setCompilerVersion] = useState('Please Select')
    const [licenseType, setLicenseType] = useState('Please Select')

    const compilerConfig = {
        'Solidity (Single file)': 'solc',
        'Solidity (Multi-Part files)': 'solc-multiple',
        'Solidity (Standard-Json-Input)': 'solc-json'
    }

    const licenseConfig = {
        '1) No License (None)': '1',
        '2) The Unlicense (Unlicense)': '2',
        '3) MIT License (MIT)': '3',
        '4) GNU General Public License v2.0 (GNU GPLv2)': '4',
        '5) GNU General Public License v3.0 (GNU GPLv3)': '5',
        '6) GNU Lesser General Public License v2.1 (GNU LGPLv2.1)': '6',
        '7) GNU Lesser General Public License v3.0 (GNU LGPLv3)': '7',
        '8) BSD 2-clause "Simplified" license (BSD-2-Clause)': '8',
        '9) BSD 3-clause "New" Or "Revised" license (BSD-3-Clause)': '9',
        '10) Mozilla Public License 2.0 (MPL-2.0)': '10',
        '11) Open Software License 3.0 (OSL-3.0)': '11',
        '12) Apache 2.0 (Apache-2.0)': '12',
        '13) GNU Affero General Public License (GNU AGPLv3)': '13',
        '14) Business Source License (BSL 1.1)': '14'
    }

    const handleAddressChange = e => {
        const value = e.target.value
        setContractAddress(value)

        if (value.trim() === '') {
            setErrors(prev => ({ ...prev, contractAddress: 'Required' }))
        } else if (value.length !== 42) {
            setErrors(prev => ({ ...prev, contractAddress: 'Invalid length' }))
        } else {
            setErrors(prev => ({ ...prev, contractAddress: '' }))
        }
    }

    return (
        <div className='page' style={{ width: isMobile ? '95%' : '60%', margin: '0 auto' }}>
            <div className='text-center'>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>Verify & Publish Contract Source Code</div>
                <div style={{ fontSize: '14px', marginTop: '8px' }}>
                    Source code verification provides transparency for users interacting with smart contracts. By
                    uploading the source code, Etherscan will match the compiled code with that on the blockchain.{' '}
                    <span
                        className={`${network.name}-text-color cursor-pointer`}
                        onClick={() => history.push(`/${network.name}/how-to-verify-contracts`)}
                    >
                        Read more
                    </span>
                    .
                </div>
            </div>

            <div className='verify-steps-container'>
                <div className='step-item'>
                    <div className={`step-indicator`} style={{ background: `var(--${network.name}-color)` }}>
                        1
                    </div>
                    <div>Enter Contract Details</div>
                </div>
                <div className='step-divider' />

                <div className='step-item'>
                    <div className={`step-indicator`} style={{ background: `var(--dark-color)` }}>
                        2
                    </div>
                    <div>Verify & Publish</div>
                </div>
            </div>

            <div className='box table-section'>
                <FormGroup>
                    <Label>Please enter the Contract Address you would like to verify</Label>
                    <Input
                        type='text'
                        placeholder='0x...'
                        className='mb-3 verify-contract-input'
                        value={contractAddress}
                        onChange={handleAddressChange}
                        invalid={!!errors.contractAddress}
                        style={{
                            backgroundColor: 'var(--dropdown-background)',
                            color: 'var(--white-color)',
                            border: 'none'
                        }}
                        required
                    />
                    {errors.contractAddress && <FormFeedback>{errors.contractAddress}</FormFeedback>}
                </FormGroup>
                <VerifyContractFormGroup
                    type='compilerType'
                    label='Please select Compiler Type'
                    value={compilerType}
                    setValue={setCompilerType}
                    errors={errors}
                    setErrors={setErrors}
                    options={compilerConfig}
                />
                <VerifyContractFormGroup
                    type='compilerVersion'
                    label='Please select Compiler Version'
                    value={compilerVersion}
                    setValue={setCompilerVersion}
                    errors={errors}
                    setErrors={setErrors}
                    options={solidityVersionOptions}
                />
                <VerifyContractFormGroup
                    type='licenseType'
                    label={
                        <>
                            Please select Open Source License Type&nbsp;
                            <span>
                                <i
                                    id='license-tooltip'
                                    className={`${network.name}-text-color fas fa-info-circle cursor-pointer`}
                                    style={{ fontSize: '14px' }}
                                    onClick={() => history.push(`/${network.name}/contract-license-types`)}
                                />
                                <UncontrolledTooltip placement='top' target='license-tooltip'>
                                    Contract Source Code License Type, click for more info.
                                </UncontrolledTooltip>
                            </span>
                        </>
                    }
                    value={licenseType}
                    setValue={setLicenseType}
                    errors={errors}
                    setErrors={setErrors}
                    options={licenseConfig}
                />
                <FormGroup check className='mb-3'>
                    <Input
                        type='checkbox'
                        id='termsCheckbox'
                        checked={true}
                        className={`${network.name}-checkbox cursor-pointer`}
                    />
                    <Label check for='termsCheckbox'>
                        I agree to the&nbsp;
                        <span
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => history.push(`/${network.name}/terms`)}
                        >
                            terms of service
                        </span>
                    </Label>
                </FormGroup>
            </div>

            <div className='d-flex justify-content-center gap-3' style={{ margin: '24px 0' }}>
                <Button
                    onClick={() => {
                        const newErrors = {
                            contractAddress: contractAddress.trim() === '' ? 'Required' : '',
                            compilerType: compilerType === 'Please Select' ? 'Required' : '',
                            compilerVersion: compilerVersion === 'Please Select' ? 'Required' : '',
                            licenseType: licenseType === 'Please Select' ? 'Required' : ''
                        }

                        setErrors(newErrors)

                        if (Object.values(newErrors).every(error => !error)) {
                            history.push(
                                `/${network.name}/verifyContract-${
                                    compilerConfig[compilerType]
                                }?a=${contractAddress}&c=${encodeURIComponent(compilerVersion).toLowerCase()}&lictype=${
                                    licenseConfig[licenseType]
                                }`
                            )
                        }
                    }}
                    style={{
                        background: `var(--${network.name}-color)`,
                        border: 'none',
                        minWidth: '120px'
                    }}
                >
                    Continue
                </Button>
                <Button
                    onClick={() => {
                        window.location.reload()
                    }}
                    style={{
                        background: 'var(--black-background)',
                        border: 'none',
                        minWidth: '120px'
                    }}
                >
                    Reset
                </Button>
            </div>
        </div>
    )
}

module.exports = VerifyContract
